import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, Instagram, MapPin, Facebook } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

const SUBSTACK_URL =
    "https://substack.com/@coralbaycatering?r=1wil5o&utm_medium=ios&utm_source=stories&shareImageVariant=blur";
const FACEBOOK_URL =
    "https://www.facebook.com/share/1FjP6AiPPj/?mibextid=wwXIfr";
const INSTAGRAM_URL = "https://instagram.com/coralbay_catering";

function SubstackIcon({ className = "w-4 h-4" }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
            <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
        </svg>
    );
}

export default function ContactPage() {
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        event_location: "",
        subject: "",
        message: "",
    });
    const [sending, setSending] = useState(false);

    // Pre-fill subject (and a starter message for wedding inquiries) from query param
    useEffect(() => {
        const s = searchParams.get("subject");
        if (s) {
            setForm((f) => ({
                ...f,
                subject: s,
                message:
                    s.toLowerCase().includes("wedding") && !f.message
                        ? "Hello — we're interested in your wedding catering. Our event date is ____ and we expect about ____ guests. We'd love to learn more about your packages."
                        : f.message,
            }));
        }
    }, [searchParams]);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success("Thanks for reaching out — we'll be in touch shortly.");
            setForm({
                name: "",
                email: "",
                phone: "",
                event_location: "",
                subject: "",
                message: "",
            });
        } catch {
            toast.error("Could not submit. Please call 340-423-6611.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="contact-page" className="pt-32 lg:pt-40 pb-24">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <span className="overline">Get in touch</span>
                    <h1 className="font-serif font-light text-5xl md:text-6xl text-forest-700 mt-5 leading-[1.05]">
                        Let's create something <em className="italic">delicious.</em>
                    </h1>
                    <p className="text-forest-700/70 leading-relaxed mt-6 max-w-md">
                        Have a custom request? Want to design a menu around your
                        guest list? Send us a note — we'll personally reply.
                    </p>

                    <ul className="mt-10 space-y-5 text-forest-700">
                        <ContactItem icon={Phone} label="340-423-6611" href="tel:3404236611" testid="contact-phone" />
                        <ContactItem
                            icon={Mail}
                            label="coralbaycatering@gmail.com"
                            href="mailto:coralbaycatering@gmail.com"
                            testid="contact-email"
                        />
                        <ContactItem
                            icon={Instagram}
                            label="@coralbay_catering"
                            href={INSTAGRAM_URL}
                            testid="contact-instagram"
                        />
                        <ContactItem
                            icon={Facebook}
                            label="Facebook"
                            href={FACEBOOK_URL}
                            testid="contact-facebook"
                        />
                        <ContactItem
                            icon={SubstackIcon}
                            label="Substack"
                            href={SUBSTACK_URL}
                            testid="contact-substack"
                        />
                        <ContactItem
                            icon={MapPin}
                            label="9901 Emmaus, St. John, USVI 00830"
                            testid="contact-address"
                        />
                    </ul>
                </div>

                <form
                    onSubmit={submit}
                    className="lg:col-span-7 bg-white border border-palm-400/30 rounded-sm p-8 lg:p-10 space-y-5"
                    data-testid="contact-form"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Field id="name" label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
                        <Field
                            id="email"
                            label="Email"
                            type="email"
                            value={form.email}
                            onChange={(v) => setForm({ ...form, email: v })}
                            required
                        />
                        <Field
                            id="phone"
                            label="Phone"
                            value={form.phone}
                            onChange={(v) => setForm({ ...form, phone: v })}
                        />
                        <Field
                            id="event_location"
                            label="Event location"
                            value={form.event_location}
                            onChange={(v) => setForm({ ...form, event_location: v })}
                        />
                    </div>
                    <Field
                        id="subject"
                        label="Subject"
                        value={form.subject}
                        onChange={(v) => setForm({ ...form, subject: v })}
                    />
                    <div>
                        <Label
                            htmlFor="message"
                            className="text-xs tracking-[0.18em] uppercase text-forest-400"
                        >
                            Message
                        </Label>
                        <Textarea
                            id="message"
                            rows={6}
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            data-testid="contact-message"
                            required
                            className="mt-2 border-palm-400/50 focus-visible:ring-forest-700"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={sending}
                        data-testid="contact-submit-btn"
                        className="bg-forest-700 text-sand-100 px-7 py-3.5 rounded-sm hover:bg-forest-900 transition-colors text-sm tracking-wide disabled:opacity-50"
                    >
                        {sending ? "Sending…" : "Send message"}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Field({ id, label, type = "text", value, onChange, required }) {
    return (
        <div>
            <Label htmlFor={id} className="text-xs tracking-[0.18em] uppercase text-forest-400">
                {label}
            </Label>
            <Input
                id={id}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                data-testid={`contact-${id}`}
                className="mt-2 border-palm-400/50 focus-visible:ring-forest-700"
            />
        </div>
    );
}

function ContactItem({ icon: Icon, label, href, testid }) {
    const body = (
        <>
            <Icon className="w-4 h-4 text-forest-400" strokeWidth={1.5} />
            <span>{label}</span>
        </>
    );
    if (href) {
        return (
            <li>
                <a
                    href={href}
                    data-testid={testid}
                    className="inline-flex items-center gap-3 underline-link"
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                >
                    {body}
                </a>
            </li>
        );
    }
    return (
        <li className="inline-flex items-center gap-3" data-testid={testid}>
            {body}
        </li>
    );
}
